/**
 * Baby Names — guest voting page
 *
 * Flow: welcome (enter name) → vote (cards for each name) → suggest → thanks
 * Reads ?code=XXXXXX from URL.
 * ContributorId is persisted in localStorage so the voter can resume.
 */
(function () {
  const API_BASE = '/apps/registry/api';

  let SESSION = null;
  let CONTRIBUTOR_ID = null;
  let VOTES = {}; // nameId → vote value (1, 0, -1)
  let CURRENT_INDEX = 0;
  let SHARE_CODE = null;

  const $ = id => document.getElementById(id);

  function esc(str) {
    if (!str) return '';
    return String(str).replace(/&/g,'&amp;').replace(/</g,'&lt;').replace(/>/g,'&gt;').replace(/"/g,'&quot;');
  }

  // ── API ───────────────────────────────────────────────────────────────────

  async function api(method, path, body) {
    const url = `${API_BASE}${path}`;
    const opts = { method, headers: { 'Content-Type': 'application/json' } };
    if (body) opts.body = JSON.stringify(body);
    const r = await fetch(url, opts);
    const data = await r.json().catch(() => ({}));
    if (!r.ok) throw new Error(data.error || `HTTP ${r.status}`);
    return data;
  }

  // ── Toast ─────────────────────────────────────────────────────────────────

  let toastTimer;
  function toast(msg) {
    const el = document.getElementById('bn-toast');
    if (!el) return;
    el.textContent = msg;
    el.classList.add('bn-toast--show');
    clearTimeout(toastTimer);
    toastTimer = setTimeout(() => el.classList.remove('bn-toast--show'), 2800);
  }

  // ── Show step ─────────────────────────────────────────────────────────────

  function show(stepId) {
    document.querySelectorAll('.bn-step').forEach(el => {
      el.style.display = el.id === stepId ? '' : 'none';
    });
  }

  // ── Welcome step ──────────────────────────────────────────────────────────

  function buildWelcome() {
    const step = $('step-welcome');
    if (!step) return;

    const storageKey = `bn-contrib-${SHARE_CODE}`;
    const saved = localStorage.getItem(storageKey);
    if (saved) {
      // Resume as existing contributor
      try {
        const { contributorId } = JSON.parse(saved);
        CONTRIBUTOR_ID = contributorId;
        show('step-vote');
        buildVoteStep();
        return;
      } catch (_) {}
    }

    step.innerHTML = `
      <div class="bn-vote-hero">
        <h1>${esc(SESSION.title)}</h1>
        ${SESSION.description ? `<p>${esc(SESSION.description)}</p>` : '<p>Vote on baby name ideas and help the family decide!</p>'}
      </div>
      <div class="bn-create" style="max-width:400px;margin:0 auto">
        <h2 style="font-size:1.4rem;margin-bottom:6px">Who are you?</h2>
        <p>We just need your name so the parents know who voted.</p>
        <div class="bn-field">
          <label>Your name</label>
          <input type="text" id="bn-voter-name" placeholder="e.g. Grandma Sarah" autofocus />
        </div>
        <div class="bn-field">
          <label>Relationship (optional)</label>
          <input type="text" id="bn-voter-rel" placeholder="e.g. Grandparent, Friend, Aunt…" />
        </div>
        <button class="bn-btn bn-btn--primary" id="bn-join-btn" style="width:100%">Start voting</button>
      </div>
    `;

    $('bn-join-btn').addEventListener('click', handleJoin);
    $('bn-voter-name').addEventListener('keydown', e => { if (e.key === 'Enter') handleJoin(); });
    show('step-welcome');
  }

  async function handleJoin() {
    const nameInput = $('bn-voter-name');
    const relInput = $('bn-voter-rel');
    const name = nameInput.value.trim();
    if (!name) { nameInput.focus(); return; }

    const btn = $('bn-join-btn');
    btn.textContent = 'Joining…';
    btn.disabled = true;

    try {
      const { contributorId } = await api('POST', `/naming/public/${SHARE_CODE}/join`, {
        name,
        relationship: relInput.value.trim() || null,
      });
      CONTRIBUTOR_ID = contributorId;
      localStorage.setItem(`bn-contrib-${SHARE_CODE}`, JSON.stringify({ contributorId, name }));
      show('step-vote');
      buildVoteStep();
    } catch (e) {
      toast(`Error: ${e.message}`);
      btn.textContent = 'Start voting';
      btn.disabled = false;
    }
  }

  // ── Vote step ─────────────────────────────────────────────────────────────

  const ACTIVE_NAMES = () => (SESSION.names || []).filter(n => n.status !== 'removed');

  function buildVoteStep() {
    const step = $('step-vote');
    if (!step) return;
    CURRENT_INDEX = 0;
    VOTES = {};
    renderVoteCard();
    show('step-vote');
  }

  function renderVoteCard() {
    const step = $('step-vote');
    if (!step) return;

    const names = ACTIVE_NAMES();
    const total = names.length;

    if (total === 0) {
      step.innerHTML = '<div class="bn-empty"><p>No names to vote on yet. Check back later!</p></div>';
      return;
    }

    if (CURRENT_INDEX >= total) {
      // All names voted on — go to suggest step or thanks
      if (SESSION.allowSuggestions) {
        buildSuggestStep();
      } else {
        buildThanksStep();
      }
      return;
    }

    const n = names[CURRENT_INDEX];
    const pct = Math.round((CURRENT_INDEX / total) * 100);

    step.innerHTML = `
      <div class="bn-vote-progress"><div class="bn-vote-progress__bar" style="width:${pct}%"></div></div>
      <div class="bn-vote-card">
        <div class="bn-vote-card__count">${CURRENT_INDEX + 1} of ${total}</div>
        <div class="bn-vote-card__name">${esc(n.name)}</div>
        ${n.gender ? `<div style="margin-bottom:8px"><span class="bn-gender bn-gender--${n.gender}">${n.gender}</span></div>` : ''}
        ${n.origin ? `<div class="bn-vote-card__origin">${esc(n.origin)}</div>` : ''}
        ${n.meaning ? `<div class="bn-vote-card__meaning">${esc(n.meaning)}</div>` : ''}
      </div>
      <div class="bn-vote-actions">
        <button class="bn-vote-btn bn-vote-btn--nope" data-vote="-1" title="Nope">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
          Nope
        </button>
        <button class="bn-vote-btn bn-vote-btn--maybe" data-vote="0" title="Maybe">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"/><line x1="8" y1="12" x2="16" y2="12"/></svg>
          Maybe
        </button>
        <button class="bn-vote-btn bn-vote-btn--love" data-vote="1" title="Love it">
          <svg viewBox="0 0 24 24" fill="currentColor" stroke="none"><path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/></svg>
          Love
        </button>
      </div>
    `;

    step.querySelectorAll('.bn-vote-btn').forEach(btn => {
      btn.addEventListener('click', () => {
        const vote = Number(btn.dataset.vote);
        btn.classList.add('bn-vote-btn--selected');
        VOTES[n.id] = vote;
        setTimeout(() => {
          CURRENT_INDEX++;
          renderVoteCard();
        }, 300);
      });
    });
  }

  // ── Suggest step ──────────────────────────────────────────────────────────

  function buildSuggestStep() {
    show('step-suggest');
    const step = $('step-suggest');
    if (!step) { buildThanksStep(); return; }

    step.innerHTML = `
      <div class="bn-vote-hero" style="margin-bottom:20px">
        <h1>Have a name in mind?</h1>
        <p>Suggest a name and the parents might add it to the shortlist.</p>
      </div>
      <div class="bn-create" style="max-width:400px;margin:0 auto">
        <div class="bn-field">
          <label>Name suggestion</label>
          <input type="text" id="bn-sugg-name" placeholder="e.g. Isla" />
        </div>
        <div class="bn-field">
          <label>Gender (optional)</label>
          <select id="bn-sugg-gender">
            <option value="">Any</option>
            <option value="girl">Girl</option>
            <option value="boy">Boy</option>
            <option value="neutral">Neutral</option>
          </select>
        </div>
        <div class="bn-field">
          <label>Why this name? (optional)</label>
          <textarea id="bn-sugg-note" placeholder="A short note for the parents…"></textarea>
        </div>
        <div style="display:flex;gap:10px">
          <button class="bn-btn bn-btn--primary" id="bn-sugg-btn">Send suggestion</button>
          <button class="bn-btn bn-btn--secondary" id="bn-skip-sugg-btn">Skip</button>
        </div>
      </div>
    `;

    $('bn-sugg-btn').addEventListener('click', handleSuggest);
    $('bn-skip-sugg-btn').addEventListener('click', () => submitVotesAndFinish());
  }

  async function handleSuggest() {
    const name = $('bn-sugg-name').value.trim();
    const btn = $('bn-sugg-btn');
    btn.textContent = 'Sending…';
    btn.disabled = true;

    try {
      if (name) {
        await api('POST', `/naming/public/${SHARE_CODE}/suggest`, {
          contributorId: CONTRIBUTOR_ID,
          name,
          gender: $('bn-sugg-gender').value || null,
          note: $('bn-sugg-note').value.trim() || null,
        });
      }
      await submitVotesAndFinish();
    } catch (e) {
      toast(`Error: ${e.message}`);
      btn.textContent = 'Send suggestion';
      btn.disabled = false;
    }
  }

  async function submitVotesAndFinish() {
    const votesArr = Object.entries(VOTES).map(([nameId, vote]) => ({ nameId, vote }));
    if (votesArr.length > 0) {
      try {
        await api('POST', `/naming/public/${SHARE_CODE}/vote`, {
          contributorId: CONTRIBUTOR_ID,
          votes: votesArr,
        });
      } catch (e) {
        console.warn('Vote submission error:', e.message);
      }
    }
    buildThanksStep();
  }

  // ── Thanks step ───────────────────────────────────────────────────────────

  function buildThanksStep() {
    const step = $('step-thanks');
    if (!step) return;

    step.innerHTML = `
      <div class="bn-thankyou">
        <div class="bn-thankyou__icon">🌿</div>
        <h2>Thanks for voting!</h2>
        <p>Your votes have been recorded. The parents will let everyone know when they've picked a name.</p>
        <a class="bn-btn bn-btn--secondary" href="/pages/baby-names-reveal?code=${esc(SHARE_CODE)}">Check the reveal page</a>
      </div>
    `;
    show('step-thanks');
  }

  // ── Closed / revealed sessions ────────────────────────────────────────────

  function handleNonOpenSession() {
    const app = $('bn-vote-app');
    if (!app) return;

    if (SESSION.status === 'revealed') {
      app.innerHTML = `
        <div class="bn-thankyou" style="padding-top:64px">
          <div class="bn-thankyou__icon">🎉</div>
          <h2>The name has been revealed!</h2>
          <p>Head to the reveal page to see the winning name.</p>
          <a class="bn-btn bn-btn--primary" href="/pages/baby-names-reveal?code=${esc(SHARE_CODE)}">See the reveal</a>
        </div>
      `;
    } else {
      app.innerHTML = `
        <div class="bn-thankyou" style="padding-top:64px">
          <div class="bn-thankyou__icon">🔒</div>
          <h2>Voting is closed</h2>
          <p>This name shortlist is no longer accepting votes.</p>
        </div>
      `;
    }
  }

  // ── Boot ──────────────────────────────────────────────────────────────────

  async function boot() {
    SHARE_CODE = new URLSearchParams(location.search).get('code');

    if (!SHARE_CODE) {
      const app = $('bn-vote-app');
      if (app) app.innerHTML = '<div class="bn-empty"><p>No voting code provided. Check your link.</p></div>';
      return;
    }

    try {
      const { session } = await api('GET', `/naming/public/${SHARE_CODE}`);
      SESSION = session;

      if (SESSION.status !== 'open') {
        handleNonOpenSession();
        return;
      }

      buildWelcome();
    } catch (e) {
      const app = $('bn-vote-app');
      if (app) app.innerHTML = `<div class="bn-empty"><p>Could not load voting session. The link may be invalid.</p></div>`;
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
